import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NutritionDashboard } from "@/components/NutritionDashboard";
import { RecipeBrowser } from "@/components/RecipeBrowser";
import { SavedMealsList } from "@/components/SavedMealsList";
import { Button } from "@/components/ui/button";
import { Plus, Heart, ChevronDown, Activity } from "lucide-react";
import { CreateMealModal } from "@/components/CreateMealModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Capacitor } from "@capacitor/core";

type NutritionView = "log-food" | "recipes" | "saved-meals";

export const FoodLog = () => {
  const [view, setView] = useState<NutritionView>("log-food");
  const [showCreateMeal, setShowCreateMeal] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

  const viewLabels: Record<NutritionView, string> = {
    "log-food": t('foodLog.logFood'),
    recipes: t('foodLog.recipes'),
    "saved-meals": t('foodLog.savedMeals'),
  };

  return (
    <div className="pb-24" style={{ background: "transparent" }}>
      <div className="max-w-7xl mx-auto">
        <div className="border-b border-white/10 p-4 sm:p-6">
          <div className="text-center mb-4">
            <h1 className="text-2xl sm:text-3xl font-normal text-foreground mb-2 flex items-center justify-center gap-2">
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 fill-primary text-primary" />
              {t('foodLog.title')}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t('foodLog.subtitle')}
            </p>
          </div>

          {/* Nutrition & Apple Health buttons */}
          <div className="max-w-2xl mx-auto flex gap-3">
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
                {t('foodLog.nutrition')}
              </h3>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="w-full justify-between bg-gradient-primary text-black shadow-glow hover:shadow-lg border-0"
                  >
                    {viewLabels[view]}
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuItem onClick={() => setView("log-food")}>
                    {t('foodLog.logFood')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setView("recipes")}>
                    {t('foodLog.recipes')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setView("saved-meals")}>
                    {t('foodLog.savedMeals')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {isIOS && (
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
                  {t('foodLog.appleHealth')}
                </h3>
                <Button
                  className="w-full bg-gradient-primary text-black shadow-glow hover:shadow-lg border-0 gap-2"
                  onClick={() => navigate("/health-data")}
                >
                  <Activity className="h-4 w-4" />
                  {t('foodLog.healthData')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-0 pt-4">
          {view === "log-food" && <NutritionDashboard />}
          {view === "recipes" && (
            <div className="p-4 sm:p-6 pt-4">
              <RecipeBrowser />
            </div>
          )}
          {view === "saved-meals" && (
            <div className="p-4 sm:p-6 pt-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-normal text-foreground">{t('foodLog.yourSavedMeals')}</h2>
                <Button onClick={() => setShowCreateMeal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('foodLog.createMeal')}
                </Button>
              </div>
              <SavedMealsList />
            </div>
          )}
        </div>
      </div>

      <CreateMealModal
        open={showCreateMeal}
        onOpenChange={setShowCreateMeal}
        editMeal={null}
      />
    </div>
  );
};
